import { CaptureOptions, JsonType } from 'posthog-js';
import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { Logger } from '@eluve/logger';

import { FeatureFlagProvider } from './FeatureFlagProvider';

/**
 * Analytics interface that components consume via useAnalytics
 */
export interface AnalyticsInstance {
  capture: (
    eventName: string,
    properties?: Record<string, unknown>,
    captureOptions?: CaptureOptions,
  ) => void;
  isFeatureEnabled: (flagKey: string) => boolean | undefined;
  getFeatureFlagPayload: (flagKey: string) => JsonType;
}
const noopAnalyticsLogger = new Logger('NoopAnalyticsInstance');
const NoopAnalyticsInstance: AnalyticsInstance = {
  capture: (eventName, meta) =>
    noopAnalyticsLogger.error(`NoopAnalyticsInstance.capture called`, {
      eventName,
      meta,
    }),
  isFeatureEnabled: (flagKey) => {
    noopAnalyticsLogger.error(`NoopAnalyticsInstance.isFeatureEnabled called`, {
      flagKey,
    });
    return false;
  },
  getFeatureFlagPayload: (flagKey) => {
    noopAnalyticsLogger.error(
      `NoopAnalyticsInstance.getFeatureFlagPayload called`,
      {
        flagKey,
      },
    );
    return undefined;
  },
};

const AnalyticsContext = createContext<AnalyticsInstance | null>(null);

export interface CoreAnalyticsProviderProps {
  children: React.ReactNode;
  analyticsInstance?: AnalyticsInstance;
}

/**
 * Core analytics provider that supplies analytics context for useAnalytics hook.
 *
 * - With analyticsInstance: Provides real analytics
 * - Without analyticsInstance: Provides stub analytics (no-op methods)
 *
 * Always wraps FeatureFlagProvider internally.
 */
export const CoreAnalyticsProvider: React.FC<CoreAnalyticsProviderProps> = ({
  children,
  analyticsInstance,
}) => {
  // Not in the logging context
  const logger = useMemo(() => new Logger('CoreAnalyticsProvider'), []);

  const analytics = useMemo<AnalyticsInstance>(() => {
    if (!analyticsInstance) {
      return NoopAnalyticsInstance;
    }

    return analyticsInstance;
  }, [analyticsInstance]);

  useEffect(() => {
    if (!analyticsInstance) {
      logger.error(
        'CoreAnalyticsProvider rendered without analyticsInstance prop. Using stub implementation.',
      );
    }
  }, [analyticsInstance, logger]);

  return (
    <AnalyticsContext.Provider value={analytics}>
      <FeatureFlagProvider>{children}</FeatureFlagProvider>
    </AnalyticsContext.Provider>
  );
};

export const useAnalyticsContext = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    return NoopAnalyticsInstance;
  }
  return context;
};
